import Data from 'data';
import { sameLimits, defaultValue, itemGetCounts, itemGetLimits } from 'logic/item';

function itemUpdateEnchant(item) {
  if (!item.imported) {
    delete item.enchant;
    return;
  }
  const required = Data.getItemAffixesById(item.id, item.ancient, "only");
  item.enchant = Object.keys(item.stats).find(stat => {
    if (!Data.stats[stat]) return false;
    if (Data.stats[stat].caldesanns) return false;
    if (required[stat] && (!item.merged || !item.merged[stat])) return false;
    const imp = item.imported.stats[stat];
    if (!imp) return true;
    if (!(item.merged && item.merged[stat]) !== !(item.imported.merged && item.imported.merged[stat])) return true;
    if (imp.length >= 1 && item.stats[stat][0] !== imp[0]) return true;
    if (imp.length >= 2 && item.stats[stat][1] !== imp[1]) return true;
    return false;
  }) || item.imported.enchant;
}

export function itemSetType(item, type) {
  const itemInfo = item && Data.itemById[item.id];
  if (!itemInfo || itemInfo.type !== type) {
    return null;
  }
  return item;
}

export function itemSetAncient(item, ancient, charClass) {
  if (!item || item.ancient === ancient) return item;
  item = {...item};
  delete item.enchant;
  delete item.imported;
  item.stats = {...item.stats};

  const prevLimits = itemGetLimits(item);
  item.ancient = ancient;
  const limits = itemGetLimits(item);

  for (const stat of Object.keys(item.stats)) {
    if (!ancient && Data.stats[stat] && Data.stats[stat].caldesanns) {
      delete item.stats[stat];
    } else if (ancient !== 'sanctified' && Data.stats[stat]?.sanctified) {
      delete item.stats[stat];
    } else if (limits[stat] && limits[stat].args > 0) {
      const prev = item.stats[stat];
      item.stats[stat] = defaultValue(limits[stat], sameLimits(limits[stat], prevLimits[stat]) ? prev : null);
    }
  }

  if (ancient === 'sanctified' && !Object.keys(item.stats).some(id => Data.stats[id]?.sanctified)) {
    const id = Object.keys(Data.stats).find(id => Data.stats[id].sanctified && Data.stats[id].class === charClass);
    if (id) item.stats[id] = [];
  }

  return item;
}

export function itemSetId(item, id, charClass) {
  if (!id || !Data.itemById[id]) return null;

  const newItem = {id, stats: {}};
  const itemInfo = Data.itemById[id];
  if (item && Data.qualities[itemInfo.quality].ancient) {
    newItem.ancient = item.ancient;
  }
  const required = Data.getItemAffixesById(id, newItem.ancient, "only");
  const affixes = Data.getItemAffixesById(id, newItem.ancient, true);
  const preset = [
    ...Data.getItemPreset(id).map(stat => Data.smartListStats(stat, affixes, charClass)).filter(list => list.length > 0),
    ...Object.keys(required).map(stat => [stat]),
  ];
  const numStats = Data.getStatCount(id);

  const slotType = Data.itemTypes[itemInfo.type] && Data.itemTypes[itemInfo.type].slot;
  const isWeapon = (slotType === "onehand" || slotType === "twohand");
  const stats = [];
  let remaining = [];
  if (item) {
    for (const stat in item.stats) {
      if (!affixes[stat] || Data.stats[stat].base) {
        continue;
      }
      if (Data.stats[stat].caldesanns) {
        stats.push(stat);
        continue;
      }
      const index = preset.findIndex(list => list.includes(stat));
      if (index >= 0) {
        preset[index] = [stat];
      } else if (stat === "sockets" && isWeapon) {
        stats.push(stat);
      } else {
        remaining.push(stat);
      }
    }
  }

  for (const [stat] of preset) {
    const group = (!affixes[stat].noblock && Data.stats[stat].group) || stat;
    remaining = remaining.filter(rem => rem !== group && Data.stats[rem].group !== group);
    if (Data.stats[stat].secondary) {
      numStats[1] -= 1;
    } else if (!Data.stats[stat].base && !(stat === "sockets" && isWeapon)) {
      numStats[0] -= 1;
    }
    stats.push(stat);
  }

  for (const stat of remaining) {
    const index = (Data.stats[stat].secondary ? 1 : 0);
    if (numStats[index] > 0) {
      stats.push(stat);
      numStats[index] -= 1;
    }
  }

  const prevLimits = item && itemGetLimits(item);
  const limits = itemGetLimits(newItem, stats);
  for (const stat of stats) {
    newItem.stats[stat] = defaultValue(limits[stat], item && sameLimits(limits[stat], prevLimits[stat]) ? item.stats[stat] : null, charClass);
  }

  if (newItem.stats.sockets) {
    newItem.gems = item?.gems?.slice(0, newItem.stats.sockets[0]) || [];
    if (newItem.gems.length < newItem.stats.sockets[0]) {
      newItem.gems.push(...Array(newItem.stats.sockets[0] - newItem.gems.length));
    }
  }

  return newItem;
}

export function itemAddStat(item) {
  const counts = itemGetCounts(item);
  const empty = Math.max(item.empty || 0, Math.max(0, counts.maxPrimary - counts.usedPrimary) + Math.max(0, counts.maxSecondary - counts.usedSecondary));
  return {...item, empty: empty + 1};
}

export function itemChangeStat(item, from, to) {
  if (from && from === to) return item;

  if (from === "custom") {
    const itemInfo = Data.itemById[item.id];
    if (itemInfo.required?.custom?.options?.includes(to)) {
      item = {...item};
      item.customId = to;
      const limits = itemGetLimits(item, [], [from]);
      if (limits.custom) {
        item.stats = {...item.stats};
        item.stats.custom = defaultValue(limits.custom);
      }
    }
    return item;
  }

  let counts = itemGetCounts(item);
  let minEmpty = Math.max(0, counts.maxPrimary - counts.usedPrimary) + Math.max(0, counts.maxSecondary - counts.usedSecondary);

  if (from == null && to == null) {
    // special case
    item = {...item};
    item.empty = Math.max((item.empty || 0) - 1, minEmpty);
    return item;
  }

  const limits = itemGetLimits(item, [from], [to]);

  item = {...item};
  item.stats = {...item.stats};

  let prevValue = null;
  if (from) {
    if (limits[from] && limits[from].merged) {
      item.merged = {...item.merged};
      delete item.merged[from];
      const newLimits = itemGetLimits(item, [from]);
      if (newLimits[from]) {
        item.stats[from] = defaultValue(newLimits[from]);
      }
      if (!Data.stats[from].caldesanns && !Data.stats[from].sanctified) {
        item.empty = Math.max(item.empty || 0, minEmpty) + 1;
      }
    } else if (item.stats[from]) {
      if (to && sameLimits(limits[from], limits[to])) {
        prevValue = item.stats[from];
      }
      delete item.stats[from];
      if (!Data.stats[from].caldesanns && !Data.stats[from].sanctified) {
        item.empty = Math.max(item.empty || 0, minEmpty) + 1;
      }
    }
  }
  counts = itemGetCounts(item);
  minEmpty = Math.max(0, counts.maxPrimary - counts.usedPrimary) + Math.max(0, counts.maxSecondary - counts.usedSecondary);
  if (to) {
    if (limits[to] && limits[to].merged) {
      item.merged = {...item.merged};
      item.merged[to] = true;
      item.stats[to] = defaultValue(limits[to]);
      if (!Data.stats[to].caldesanns && !Data.stats[to].sanctified) {
        item.empty = Math.max(Math.max(item.empty || 0, minEmpty) - 1, 0);
      }
    } else if (!item.stats[to]) {
      if (prevValue) {
        item.stats[to] = prevValue;
      } else {
        item.stats[to] = defaultValue(limits[to]);
      }
      if (!Data.stats[to].caldesanns && !Data.stats[to].sanctified) {
        item.empty = Math.max(Math.max(item.empty || 0, minEmpty) - 1, 0);
      }
    }
  }

  if (!item.stats.sockets && item.gems) {
    delete item.gems;
  }
  if (item.stats.sockets && !item.gems) {
    item.gems = [...new Array(item.stats.sockets[0])];
  }

  itemUpdateEnchant(item);
  return item;
}

export function itemSetStatValue(item, stat, index, value) {
  if (!item.stats[stat]) return item;
  const limits = itemGetLimits(item, [stat])[stat];
  if (limits && limits.args > index) {
    const min = index ? limits.min2 : limits.min;
    const max = index ? limits.max2 : limits.max;
    const step = (index ? limits.step2 : limits.step) || 1;
    if (!step || !isNaN(step)) value = Math.round(value / step) * step;
    if (min != null) value = Math.max(min, value);
    if (max != null) value = Math.min(max, value);
  }
  if (item.stats[stat][index] === value) return item;
  item = {...item};
  item.stats = {...item.stats};
  item.stats[stat] = item.stats[stat].slice();
  item.stats[stat][index] = value;
  if (stat === "sockets") {
    if (!item.gems) {
      item.gems = [...new Array(value)];
    } else if (item.gems.length > value) {
      item.gems = item.gems.slice(0, value);
    } else if (item.gems.length < value) {
      item.gems = [...item.gems, ...Array(value - item.gems.length)];
    }
  }
  itemUpdateEnchant(item);
  return item;
}

export function itemSetGem(item, index, gem) {
  if (!item.gems || index >= item.gems.length) return item;
  if (item.gems[index] === gem) return item;
  item = {...item};
  item.gems = item.gems.slice();
  item.gems[index] = gem;
  return item;
}

export function itemSetGemType(item, index, type, charClass) {
  if (!item.gems || index >= item.gems.length) return item;
  const prev = item.gems[index];
  if (Array.isArray(prev) && prev[0] === type) return item;
  if (prev && prev.id === type) return item;
  item = {...item};
  item.gems = item.gems.slice();
  if (type == null) {
    item.gems[index] = undefined;
  } if (Data.legendaryGems[type]) {
    const level = (Array.isArray(prev) && Data.legendaryGems[prev[0]] ? prev[1] : 0);
    item.gems[index] = [type, level];
  } else if (Data.gemQualities[type]) {
    const color = (Array.isArray(prev) && !Data.legendaryGems[prev[0]] ? prev[1] : Object.keys(Data.gemColors)[0]);
    item.gems[index] = [type, color];
  } else if (Data.itemById[type]) {
    item.gems[index] = itemSetId(prev && prev.id ? prev : null, type, charClass);
  }
  return item;
}

export function itemSetGemColor(item, index, color) {
  if (!item.gems || !item.gems[index]) return item;
  if (!Data.gemColors[color]) return item;
  item = {...item};
  item.gems = item.gems.slice();
  item.gems[index] = [item.gems[index][0], color];
  for (let i = 0; i < item.gems.length; ++i) {
    if (!item.gems[i]) {
      item.gems[i] = item.gems[index].slice();
    }
  }
  return item;
}

export function itemSetGemLevel(item, index, level) {
  if (!item.gems || !item.gems[index]) return item;
  const gem = Data.legendaryGems[item.gems[index][0]];
  if (gem) {
    level = Math.max(0, level);
    level = Math.min(gem.maxlevel || 150, level);
  }
  if (item.gems[index][1] === level) return item;
  item = {...item};
  item.gems = item.gems.slice();
  item.gems[index] = [item.gems[index][0], level];
  return item;
}

export function itemSetImported(item, stat) {
  return {
    ...item,
    enchant: stat,
    imported: {
      stats: item.stats,
      enchant: stat,
      merged: item.merged,
    },
  };
}

export function itemImportUnlock(item) {
  if (!item.imported) return item;
  item = {...item};
  delete item.imported;
  delete item.enchant;
  return item;
}

export function itemImportReset(item) {
  if (!item.imported) return item;
  item = {...item};
  item.stats = item.imported.stats;
  item.enchant = item.imported.enchant;
  item.merged = item.imported.merged;
  return item;
}

export function itemFix(item) {
  if (!item) return item;
  item = {...item};
  const origStats = item.stats;
  if (item.stats.sockets) {
    const count = item.stats.sockets[0];
    if (!item.gems) {
      item.gems = [...Array(count)];
    } else if (item.gems.length > count) {
      item.gems = item.gems.slice(0, count);
    } else if (item.gems.length < count) {
      item.gems = [...item.gems, ...Array(count - item.gems.length)];
    }
  }
  if (item.gems) {
    item.gems = item.gems.map(gem => {
      if (Array.isArray(gem)) {
        if (Data.legendaryGems[gem[0]]) return [gem[0], parseInt(gem[1])];
        else if (Data.gemQualities[gem[0]] && Data.gemColors[gem[1]]) return [gem[0], gem[1]];
        else return null;
      } else if (gem && gem.id && Data.itemById[gem.id]) {
        return itemFix(gem);
      } else {
        return null;
      }
    });
  }
  if (item.stats.custom) {
    const base = Data.itemById[item.id];
    if (base?.required.extra_passive && !base?.required.custom) {
      item.stats = {...item.stats};
      item.stats.extra_passive = item.stats.custom;
      delete item.stats.custom;
    }
  }
  if (!item.merged) {
    // check if some stats are too high
    const required = Data.getItemAffixesById(item.id, item.ancient, "only");
    const merged = Object.keys(item.stats).filter(stat => {
      if (!required[stat] || !required[stat].noblock || (required[stat].args || 1) < 1) return false;
      return item.stats[stat][0] > required[stat].max;
    });
    if (merged.length) {
      // shouldn't be possible to have more than one merged stat, but let's support this case anyways
      item.merged = {};
      for (const stat of merged) {
        item.merged[stat] = true;
      }
    } else if (item.imported) {
      // if an imported item is missing stats, we assume it's merged
      const counts = itemGetCounts(item);
      if (counts.usedPrimary < counts.maxPrimary) {
        const merged = Object.keys(item.stats).find(stat => {
          if (!required[stat] || !required[stat].noblock || (required[stat].args || 1) < 1) return false;
          return true;
        });
        if (merged) {
          item.merged = {[merged]: true};
        }
      }
    }
    if (item.merged && item.imported) {
      if (!item.merged[item.enchant] || item.imported.enchant === item.enchant) {
        item.imported = {...item.imported, merged: item.merged};
      }
    }
  }
  // validate other stats
  const limits = itemGetLimits(item);
  for (const stat in item.stats) {
    if (!limits[stat]) continue;
    const { min=0, max=Infinity, min2=0, max2=Infinity, args=1 } = limits[stat];
    if (args <= 0) continue;
    const prev = item.stats[stat];
    if (prev[0] < min || prev[0] > max || (args >= 2 && (prev[1] < min2 || prev[1] > max2))) {
      if (item.stats === origStats) item.stats = {...item.stats};
      item.stats[stat] = defaultValue(limits[stat], prev);
    }
  }
  return item;
}

export function itemSetStatsPercent(item, percent, unlock) {
  item = {...item};
  if (unlock) {
    delete item.enchant;
    delete item.imported;
  }
  item.stats = {...item.stats};
  const limits = itemGetLimits(item);
  for (const stat of Object.keys(item.stats)) {
    if (!item.stats[stat]) continue;
    if (item.imported && item.enchant !== stat) continue;
    if (Data.stats[stat] && Data.stats[stat].caldesanns) continue;
    if (limits[stat] && limits[stat].args > 0) {
      item.stats[stat] = defaultValue(limits[stat], null, null, percent);
    }
  }
  return item;
}

export function itemSetAugment(item, stat, level) {
  if (!item.ancient) return item;
  item = {...item};
  item.stats = {...item.stats};
  for (const stat of Object.keys(item.stats)) {
    if (Data.stats[stat] && Data.stats[stat].caldesanns) {
      delete item.stats[stat];
    }
  }
  if (stat && level) {
    item.stats[stat] = [level * 5];
  }
  return item;
}
